/**
 * Team → Timesheets (prototype VIEWS.tsreport + the step-4 "TIMESHEETS: day, week and month" block).
 *
 *  - Day: who filed on time, late, or not at all for one working day, with "Review and edit" / "File for them"
 *    (PATCH /timesheets/{user_id}/{date}, in the manager's own name) and a WhatsApp nudge (POST /nudges).
 *  - Week / Month: stat strip, then Week grid | Month grid, Analysis (founder: `analysis` is present only with
 *    timesheet.analysis.read), Manager feedback, Automation ideas.
 *
 * Every number comes from GET /timesheets (cells carry `expected_hours` from the published calendar, so the
 * SPA never decides what a working day is) or the step-4 feedback / automation routes.
 */
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { ROLE_LABEL, type TimesheetsResponse, type UserRef } from '@amplispot/contracts';
import { capabilitiesFor } from '@amplispot/domain';
import { useSession } from '../../lib/session';
import { useClients } from '../../lib/data';
import { firstName, todayIso } from '../../lib/format';
import { addDays } from '../../lib/work';
import {
  fh,
  istTime,
  lastMonths,
  lastWeeks,
  monthEnd,
  monthLabel,
  monthPeriod,
  mondayOf,
  r1,
  shortDay,
  useTimesheetGrid,
  useWorkingDays,
  weekLabel,
  weekPeriod,
} from '../../lib/timesheets';
import { Avatar, Empty, ErrorState, Loading, Pills, Seg, useToast } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';
import { TsEditModal, WhatsAppModal, copyText } from './TsModals';
import { TsFeedback } from './TsFeedback';
import { TsAutomate } from './TsAutomate';
import { LabelInfo, Lbl } from '../../shell/Help';

/** Cell padding for the half-width "Plan against what happened" table (five columns must fit). */
const TP = { padding: '12px 10px' } as const;

type Mode = 'day' | 'week' | 'month';
type Tab = 'grid' | 'analysis' | 'feedback' | 'automate';
type Row = TimesheetsResponse['grid'][number];
type Cell = Row['cells'][number];

const STATUS: Record<Cell['status'], [string, string]> = {
  in: ['hot', 'On time'],
  late: ['warm', 'Late'],
  missing: ['cold', 'Not filed'],
  open: ['', 'Due by 6:30 pm'],
  not_expected: ['', 'Day off'],
};

export function TimesheetReport() {
  const today = todayIso();
  const [sp, setSp] = useSearchParams();
  const mode = (['day', 'week', 'month'].includes(sp.get('view') ?? '') ? sp.get('view') : 'day') as Mode;
  const weeks = useMemo(() => lastWeeks(today, 8), [today]);
  const months = useMemo(() => lastMonths(today, 6), [today]);
  const wk = sp.get('week') && weeks.includes(sp.get('week')!) ? sp.get('week')! : weeks[0]!;
  const mo = sp.get('month') && months.includes(sp.get('month')!) ? sp.get('month')! : months[0]!;
  const set = (k: string, v: string) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set(k, v);
      return n;
    });

  return (
    <>
      <div className="split" style={{ margin: '0 0 16px', gap: 14, flexWrap: 'wrap' }}>
        <Seg<Mode> options={[['day', 'Day'], ['week', 'Week'], ['month', 'Month']]} value={mode} onChange={(v) => set('view', v)} />
        {mode === 'week' ? (
          <select className="in" style={{ width: 'auto', borderRadius: 99 }} value={wk} onChange={(e) => set('week', e.target.value)} aria-label="Week">
            {weeks.map((w) => (
              <option key={w} value={w}>
                {weekLabel(w)}
              </option>
            ))}
          </select>
        ) : mode === 'month' ? (
          <select className="in" style={{ width: 'auto', borderRadius: 99 }} value={mo} onChange={(e) => set('month', e.target.value)} aria-label="Month">
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m, today)}
              </option>
            ))}
          </select>
        ) : null}
        <div className="grow" />
      </div>
      {mode === 'day' ? <DayView /> : <PeriodView mode={mode} from={mode === 'week' ? wk : mo} to={mode === 'week' ? addDays(wk, 6) : monthEnd(mo)} />}
    </>
  );
}

// ---------------------------------------------------------------- Day

function DayView() {
  const today = todayIso();
  const { me } = useSession();
  const [sp, setSp] = useSearchParams();
  // The last working days come from the published calendar (GET /working-days), never from weekday maths here.
  // Picked by kind, not the viewer's own expected hours: those are 0 before the viewer joined, and the team's days are
  // still working days then (t-bed4f239).
  const wd = useWorkingDays(addDays(today, -21), today);
  const days = useMemo(
    () =>
      (wd.data?.days ?? [])
        .filter((d) => d.kind === 'working' || d.kind === 'half_day' || d.kind === 'optional_available')
        .map((d) => d.date)
        .reverse()
        .slice(0, 6),
    [wd.data],
  );
  const prev = days.find((d) => d < today) ?? days[0] ?? addDays(today, -1);
  const day = sp.get('day') && days.includes(sp.get('day')!) ? sp.get('day')! : prev;
  const grid = useTimesheetGrid('day', day, day, !!day);
  const rows = grid.data?.grid ?? [];
  const [edit, setEdit] = useState<{ user: UserRef; cell: Cell } | null>(null);
  const [wa, setWa] = useState<{ user: UserRef; missing: boolean; gated: boolean } | null>(null);
  const toast = useToast();

  if (wd.isLoading || grid.isLoading) return <Loading />;
  if (wd.error) return <ErrorState error={wd.error} onRetry={() => void wd.refetch()} />;
  if (grid.error) return <ErrorState error={grid.error} onRetry={() => void grid.refetch()} />;
  const exp = grid.data?.expected_per_day ?? me.config.working_day_hours;
  const cellOf = (r: Row) => r.cells[0]!;
  const n = (s: Cell['status']) => rows.filter((r) => cellOf(r).status === s).length;
  const filed = rows.filter((r) => cellOf(r).hours != null);
  const avg = filed.length ? r1(filed.reduce((a, r) => a + (cellOf(r).hours ?? 0), 0) / filed.length) : 0;
  const expectedRows = rows.filter((r) => cellOf(r).status !== 'not_expected');
  // Whether the timesheet gate applies (the row's `gated`, from capability timesheet.gated; an older server
  // without it falls back to the role's capabilities). The Ops AD files but is never gated (operator ruling
  // 2026-10-16), so their missed day is "not filed", never "locked out" (t-06836937).
  const isGated = (r: Row) => r.gated ?? capabilitiesFor(r.user.role).includes('timesheet.gated');
  const locked = rows.filter((r) => cellOf(r).status === 'missing' && isGated(r));
  const lockedOut = locked.length;

  const message = () => {
    const g = (s: Cell['status']) => rows.filter((r) => cellOf(r).status === s).map((r) => firstName(r.user.name));
    return [
      `*Timesheets, ${shortDay(day)}*`,
      g('in').length ? `Filed on time: ${g('in').join(', ')}` : '',
      g('late').length ? `Filed late: ${g('late').join(', ')}` : '',
      g('missing').length
        ? `Not filed: ${g('missing').join(', ')}.${
            !lockedOut ? '' : lockedOut === g('missing').length
              ? ` ${lockedOut > 1 ? 'Their' : 'The'} next day stays locked until they file.`
              : ` Locked out of the next day until they file: ${locked.map((r) => firstName(r.user.name)).join(', ')}.`
          }`
        : '',
      filed.length ? `Hours logged: ${filed.map((r) => `${firstName(r.user.name)} ${cellOf(r).hours}h`).join(', ')}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  };

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn" onClick={() => copyText(message(), () => toast('Copied. Paste it into WhatsApp.'))} disabled={!rows.length}>
            Copy as a WhatsApp message
          </button>
        </div>
      </div>
      <div className="bar">
        <Pills<string>
          options={days.map((d) => [d, d === today ? 'Today' : shortDay(d)])}
          value={day}
          onChange={(d) =>
            setSp((p) => {
              const x = new URLSearchParams(p);
              x.set('day', d);
              return x;
            })
          }
        />
      </div>
      <div className="stats">
        <Stat k="Filed on time" v={`${n('in')} of ${expectedRows.length}`} cls={expectedRows.length && n('in') === expectedRows.length ? 'green' : ''} />
        <Stat k="Filed late" v={n('late')} cls={n('late') ? 'amber' : ''} />
        <Stat k="Not filed" v={n('missing')} s={lockedOut && lockedOut === n('missing') ? 'locked out until they file' : lockedOut ? `${lockedOut} locked out until they file` : n('missing') ? 'not filed yet' : n('open') ? `${n('open')} still due today` : ''} cls={n('missing') ? 'red' : 'green'} />
        <Stat k="Average hours logged" v={`${avg} h`} s={`of ${exp}`} />
      </div>
      <div className="panel" style={{ overflowX: 'auto' }}>
        {rows.length ? (
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>
                  <Lbl>Plan confirmed</Lbl>
                </th>
                <th>
                  <Lbl>Timesheet</Lbl>
                </th>
                <th className="tr">
                  <Lbl>Logged / planned</Lbl>
                </th>
                <th className="tr">
                  <Lbl>Tasks done</Lbl>
                </th>
                <th className="tr">
                  <Lbl>Other work</Lbl>
                </th>
                <th>
                  <Lbl>Their note</Lbl>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = cellOf(r);
                const [tone, label] = STATUS[c.status];
                const miss = c.status === 'missing';
                // Only gated filers (capability timesheet.gated, operator ruling 2026-10-16) are locked out; the Ops AD
                // files but is never gated, so their missed day is offered as a plain "not filed" reminder.
                const gated = isGated(r);
                // The day-table columns (GET /timesheets, view=day → row.day); absent only on an older server.
                const d = r.day;
                const dash = <span className="sub">-</span>;
                const planned = d ? r1(d.planned_minutes / 60) : null;
                return (
                  <tr key={r.user.id} style={miss ? { background: 'var(--cold-bg)' } : undefined}>
                    <td>
                      <div className="split">
                        <Avatar user={r.user} />
                        <div>
                          <b>{firstName(r.user.name)}</b>
                          <div className="sub" style={{ whiteSpace: 'nowrap' }}>
                            {ROLE_LABEL[r.user.role]}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{d?.plan_confirmed_at ? istTime(d.plan_confirmed_at) : <span className="sub">Not confirmed</span>}</td>
                    <td>
                      <span className={`chip ${tone}`}>
                        {label}
                        {d?.filed_at ? ` · ${istTime(d.filed_at)}` : ''}
                      </span>
                      {d?.filed_by || d?.edited_by ? (
                        <div className="sub" style={{ marginTop: 3, fontSize: 11.5 }}>
                          {d.filed_by ? `Filed by ${firstName(d.filed_by.name)}` : `Edited by ${firstName(d.edited_by!.name)}`}
                        </div>
                      ) : null}
                      {miss && gated ? (
                        <div className="sub" style={{ marginTop: 3 }}>
                          Locked out of the next day
                        </div>
                      ) : null}
                    </td>
                    <td className="tr num">{miss || c.hours == null ? '-' : planned ? `${c.hours} / ${planned} h` : `${c.hours} h`}</td>
                    <td className="tr num">{c.hours == null || !d ? '-' : d.tasks_planned ? `${d.tasks_done} of ${d.tasks_planned}` : d.tasks_done ? String(d.tasks_done) : '-'}</td>
                    <td className="tr num">{c.hours == null || !d ? '-' : `${d.other_minutes} min`}</td>
                    <td style={{ width: 150, minWidth: 130, whiteSpace: 'normal', lineHeight: 1.45 }}>{!miss && d?.note ? clip(d.note, 70) : dash}</td>
                    <td className="tr" style={{ width: 120 }}>
                      {/* Prototype: the row actions stack in a narrow column, the nudge caption under them. */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      {c.status !== 'not_expected' && c.status !== 'open' ? (
                        <button className="btn sm" style={{ whiteSpace: 'normal', maxWidth: 110 }} onClick={() => setEdit({ user: r.user, cell: c })}>
                          {miss ? (r.user.id === me.user.id ? 'File it' : 'File for them') : 'Review and edit'}
                        </button>
                      ) : null}
                      {r.user.id !== me.user.id ? (
                        <button className={`btn sm${miss ? ' pri' : ''}`} onClick={() => setWa({ user: r.user, missing: miss, gated })}>
                          WhatsApp
                        </button>
                      ) : null}
                      {d?.last_nudge ? (
                        <div className="sub" style={{ fontSize: 11, textAlign: 'right', maxWidth: 110 }}>
                          Nudged {nudgedAt(d.last_nudge.at, today)}
                          {d.last_nudge.count > 1 ? ` · ${d.last_nudge.count} times` : ''}
                        </div>
                      ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty title="Nobody files a timesheet yet">Ops members appear here once they are added in Settings → People.</Empty>
        )}
      </div>
      {edit ? <TsEditModal user={edit.user} date={day} cell={edit.cell} onClose={() => setEdit(null)} /> : null}
      {wa ? <WhatsAppModal user={wa.user} missingDay={wa.missing ? day : null} gated={wa.gated} onClose={() => setWa(null)} /> : null}
    </>
  );
}

/** "70 chars…" — the prototype's clip(). */
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trim()}…` : s);
/** "4:12 pm" today, else "Thu 15 Oct, 4:12 pm" (IST). */
const nudgedAt = (ts: string, today: string) => {
  const date = new Date(ts).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  return date === today ? istTime(ts) : `${shortDay(date)}, ${istTime(ts)}`;
};

export function Stat({ k, v, s, cls }: { k: string; v: string | number; s?: string; cls?: string }) {
  return (
    <div className={`stat ${cls ?? ''}`}>
      <div className="k">
        {k}
        <LabelInfo text={k} />
      </div>
      <div className="v">{v}</div>
      {s ? <div className="s">{s}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Week / Month

export interface PersonAgg {
  user: UserRef;
  logged: number;
  gap: number;
  late: number;
  missing: number;
  expected: number;
  /** Plan hours and planned tasks over the period (server totals), for "Plan against what happened". */
  planned: number;
  tasksDone: number;
  tasksPlanned: number;
}

/** Per-person and team totals for a period, from the grid cells (days after today are not counted yet). */
export function aggregate(data: TimesheetsResponse, today: string) {
  const ppl: PersonAgg[] = data.grid.map((r) => {
    const past = r.cells.filter((c) => c.date <= today);
    return {
      user: r.user,
      logged: r.totals.logged,
      gap: r.totals.not_logged,
      late: past.filter((c) => c.status === 'late').length,
      missing: past.filter((c) => c.status === 'missing').length,
      expected: past.filter((c) => c.date < today).reduce((a, c) => a + c.expected_hours, 0),
      planned: r.totals.planned_hours,
      tasksDone: r.totals.tasks_done,
      tasksPlanned: r.totals.tasks_planned,
    };
  });
  const sum = (f: (p: PersonAgg) => number) => ppl.reduce((a, p) => a + f(p), 0);
  return { ppl, tot: { logged: sum((p) => p.logged), gap: sum((p) => p.gap), late: sum((p) => p.late), missing: sum((p) => p.missing), expected: sum((p) => p.expected) } };
}

function PeriodView({ mode, from, to }: { mode: 'week' | 'month'; from: string; to: string }) {
  const today = todayIso();
  const { can } = useSession();
  const [sp, setSp] = useSearchParams();
  const grid = useTimesheetGrid(mode, from, to);
  const per = mode === 'week' ? weekPeriod(from) : monthPeriod(from);
  const hasAnalysis = !!grid.data?.analysis;
  const tabs: [Tab, string][] = [
    ['grid', mode === 'week' ? 'Week grid' : 'Month grid'],
    ...(hasAnalysis ? ([['analysis', 'Analysis']] as [Tab, string][]) : []),
    ['feedback', 'Manager feedback'],
    ['automate', 'Automation ideas'],
  ];
  const tabQ = sp.get('tab') as Tab | null;
  const tab: Tab = tabQ && tabs.some(([k]) => k === tabQ) ? tabQ : 'grid';

  if (grid.isLoading) return <Loading />;
  if (grid.error) return <ErrorState error={grid.error} onRetry={() => void grid.refetch()} />;
  const data = grid.data!;
  const A = aggregate(data, today);
  const T = A.tot;
  const pct = T.expected ? Math.round((T.logged / T.expected) * 100) : 0;
  const exp = data.expected_per_day;

  return (
    <>
      <div className="stats">
        <Stat k="Hours logged" v={fh(T.logged)} s={`of ${fh(T.expected)} available (${pct}%)`} cls={pct >= 95 ? 'green' : pct >= 85 ? '' : 'amber'} />
        <Stat k="Hours not logged" v={fh(T.gap)} s={`against a ${exp} h day`} cls={T.gap > T.expected * 0.1 ? 'red' : 'amber'} />
        <Stat k="Days not filed" v={T.missing} s="person-days" cls={T.missing ? 'red' : 'green'} />
        <Stat k="Filed late" v={T.late} s="person-days" cls={T.late ? 'amber' : 'green'} />
      </div>
      <div className="bar" style={{ marginTop: 0 }}>
        <Pills<Tab>
          options={tabs}
          value={tab}
          onChange={(t) =>
            setSp((p) => {
              const n = new URLSearchParams(p);
              n.set('tab', t);
              return n;
            })
          }
        />
      </div>
      {tab === 'grid' ? (
        <TsGrid data={data} mode={mode} A={A} />
      ) : tab === 'analysis' ? (
        <TsAnalysis data={data} A={A} />
      ) : tab === 'feedback' ? (
        <TsFeedback period={per} A={A} canWrite={can('timesheet.feedback.write')} />
      ) : (
        <TsAutomate month={from.slice(0, 7)} />
      )}
    </>
  );
}

function TsGrid({ data, mode, A }: { data: TimesheetsResponse; mode: 'week' | 'month'; A: ReturnType<typeof aggregate> }) {
  const today = todayIso();
  const exp = data.expected_per_day;
  // Columns: the period's days up to today that are a working day for anyone (weekends and holidays drop out).
  const dates = (data.grid[0]?.cells ?? [])
    .map((c) => c.date)
    .filter((d) => d <= today && data.grid.some((r) => r.cells.find((c) => c.date === d)?.status !== 'not_expected'));
  const isM = mode === 'month';
  const cols = isM
    ? [...new Set(dates.map(mondayOf))].map((w) => ({ k: `Wk ${weekLabel(w).replace('Week of ', '')}`, dates: dates.filter((d) => mondayOf(d) === w) }))
    : dates.map((d) => ({ k: shortDay(d), dates: [d] }));
  if (!data.grid.length) return <div className="panel"><Empty title="Nobody files a timesheet yet">Ops members appear here once they are added.</Empty></div>;
  if (!cols.length)
    return (
      <div className="panel">
        <Empty title="No working days in this period yet">Pick an earlier week or month.</Empty>
      </div>
    );

  const cell = (r: Row, ds: string[]) => {
    const cs = ds.map((d) => r.cells.find((c) => c.date === d)!).filter(Boolean);
    if (!isM) {
      const c = cs[0]!;
      if (c.status === 'missing')
        return (
          <td className="tg miss">
            <b>Not filed</b>
          </td>
        );
      if (c.status === 'not_expected')
        return (
          <td className="tg">
            <b>–</b>
            <small>day off</small>
          </td>
        );
      if (c.status === 'open')
        return (
          <td className="tg">
            <b>{c.hours ?? '–'}</b>
            <small>due today</small>
          </td>
        );
      return (
        <td className={`tg ${c.status === 'late' ? 'late' : ''}`}>
          <b>{c.hours}</b>
          {/* Prototype tsGrid: a filed day shows "of {planned}" only when the plan is non-zero (r.planned truthy; an empty plan is 0). */}
          <small>{c.status === 'late' ? 'late' : c.planned_hours ? `of ${r1(c.planned_hours)}` : ''}</small>
        </td>
      );
    }
    const lg = cs.reduce((a, c) => a + (c.hours ?? 0), 0);
    const ex = cs.reduce((a, c) => a + c.expected_hours, 0);
    const ms = cs.filter((c) => c.status === 'missing').length;
    const lt = cs.filter((c) => c.status === 'late').length;
    return (
      <td className={`tg ${ms ? 'miss' : lt ? 'late' : ''}`}>
        <b>{r1(lg)}</b>
        <small>
          of {r1(ex)}
          {ms ? ` · ${ms} not filed` : lt ? ` · ${lt} late` : ''}
        </small>
      </td>
    );
  };

  return (
    <>
      <div className="panel" style={{ overflowX: 'auto' }}>
        <table className="tgt">
          <thead>
            <tr>
              <th>Person</th>
              {cols.map((c) => (
                <th key={c.k} className="tc">
                  {c.k}
                </th>
              ))}
              <th className="tc">Total</th>
              <th className="tc">Not logged</th>
            </tr>
          </thead>
          <tbody>
            {data.grid.map((r, i) => {
              const a = A.ppl[i]!;
              const tone = a.gap > a.expected * 0.12 ? 'cold' : a.gap > a.expected * 0.05 ? 'warm' : 'hot';
              return (
                <tr key={r.user.id}>
                  <td>
                    <div className="split">
                      <Avatar user={r.user} />
                      <div>
                        <b>{firstName(r.user.name)}</b>
                        <div className="sub" style={{ whiteSpace: 'nowrap' }}>
                          {ROLE_LABEL[r.user.role]}
                        </div>
                      </div>
                    </div>
                  </td>
                  {cols.map((c) => (
                    <Fragment key={c.k}>{cell(r, c.dates)}</Fragment>
                  ))}
                  <td className="tc">
                    <b>{fh(a.logged)}</b>
                  </td>
                  <td className="tc">
                    <span className={`chip ${tone}`}>{fh(a.gap)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sub" style={{ fontSize: 12.5, marginTop: 10 }}>
        Hours are as filed. A day not filed counts as {exp} h not logged. Press the Day view to correct a single day.
      </div>
    </>
  );
}

function Bar({ label, v, max, col, sub }: { label: ReactNode; v: number; max: number; col?: string; sub: ReactNode }) {
  return (
    <div className="rb">
      <div className="rl">{label}</div>
      <div className="rt">
        <i style={{ width: `${max ? Math.max(1.5, (v / max) * 100) : 0}%`, background: col ?? 'var(--brand)' }} />
      </div>
      <div className="rv">{sub}</div>
    </div>
  );
}

function TsAnalysis({ data, A }: { data: TimesheetsResponse; A: ReturnType<typeof aggregate> }) {
  const an = data.analysis!;
  const clients = useClients();
  const [split, setSplit] = useState<'client' | 'spoc'>('client');
  const byId = new Map((clients.data ?? []).map((c) => [c.id, c]));
  const rows = [...an.by_client].sort((a, b) => b.hours - a.hours);
  const totC = rows.reduce((a, r) => a + r.hours, 0);
  const mx = rows[0]?.hours ?? 1;
  // "Time per client, split by SPOC": the same hours, grouped under who looks after each client.
  const spocs = new Map<string, { user: UserRef; hours: number; n: number }>();
  for (const r of rows) {
    const s = byId.get(r.client.id)?.spoc;
    if (!s) continue;
    const e = spocs.get(s.id) ?? { user: s, hours: 0, n: 0 };
    e.hours += r.hours;
    e.n += 1;
    spocs.set(s.id, e);
  }
  const spocRows = [...spocs.values()].sort((a, b) => b.hours - a.hours);
  const mxs = spocRows[0]?.hours ?? 1;
  const mxp = Math.max(1, ...A.ppl.map((p) => p.logged + p.gap));
  const pays = rows
    .map((r) => {
      const c = byId.get(r.client.id);
      const mrr = c?.annual_revenue_lakh != null ? c.annual_revenue_lakh / 12 : null;
      return { r, mrr, hpl: mrr ? r.hours / mrr : 0 };
    })
    .filter((x) => x.mrr)
    .sort((a, b) => b.hpl - a.hpl);

  return (
    <>
      <div className="cols c2" style={{ gap: 22, alignItems: 'start' }}>
        <div className="panel">
          <div className="hd">
            <h3>Time on each client</h3>
            <div className="r">{fh(totC)} of client work</div>
          </div>
          <div className="bd" style={{ display: 'grid', gap: 12 }}>
            <Seg<'client' | 'spoc'> options={[['client', 'By client'], ['spoc', 'By SPOC']]} value={split} onChange={setSplit} />
            {split === 'client' ? (
              rows.length ? (
                rows.map((r) => {
                  const s = byId.get(r.client.id)?.spoc;
                  return (
                    <Bar
                      key={r.client.id}
                      label={
                        <span className="split" style={{ gap: 8 }}>
                          <ClientLogo id={r.client.id} name={r.client.name} size={24} />
                          <span style={{ minWidth: 0 }}>
                            {r.client.name}
                            {s ? <span className="sub" style={{ display: 'block', fontSize: 11.5, fontWeight: 500 }}>{firstName(s.name)}</span> : null}
                          </span>
                        </span>
                      }
                      v={r.hours}
                      max={mx}
                      sub={
                        <>
                          {fh(r.hours)} <small>{totC ? Math.round((r.hours / totC) * 100) : 0}%</small>
                        </>
                      }
                    />
                  );
                })
              ) : (
                <Empty title="No client time logged in this period" />
              )
            ) : spocRows.length ? (
              spocRows.map((s) => (
                <Bar
                  key={s.user.id}
                  label={
                    <span className="split" style={{ gap: 8 }}>
                      <Avatar user={s.user} size={24} />
                      <span>
                        {firstName(s.user.name)}
                        <span className="sub" style={{ display: 'block', fontSize: 11.5, fontWeight: 500 }}>
                          {s.n} {s.n === 1 ? 'client' : 'clients'}
                        </span>
                      </span>
                    </span>
                  }
                  v={s.hours}
                  max={mxs}
                  sub={
                    <>
                      {fh(s.hours)} <small>{totC ? Math.round((s.hours / totC) * 100) : 0}%</small>
                    </>
                  }
                />
              ))
            ) : (
              <Empty title="No client time logged in this period" />
            )}
            <div className="sub" style={{ fontSize: 12 }}>
              Also: {fh(an.internal_hours)} on internal work and other work that was not in the plan.
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gap: 22 }}>
          <div className="panel">
            <div className="hd">
              <h3>Logged and not logged, by person</h3>
            </div>
            <div className="bd" style={{ display: 'grid', gap: 12 }}>
              {A.ppl.map((p) => (
                <div className="rb" key={p.user.id}>
                  <div className="rl">
                    <span className="split" style={{ gap: 8 }}>
                      <Avatar user={p.user} size={24} />
                      <span>{firstName(p.user.name)}</span>
                    </span>
                  </div>
                  <div className="rt two">
                    <i style={{ width: `${(p.logged / mxp) * 100}%`, background: 'var(--brand)' }} />
                    <i style={{ width: `${(p.gap / mxp) * 100}%`, background: '#F6C9D2' }} />
                  </div>
                  <div className="rv">
                    {fh(p.logged)} <small>{fh(p.gap)} gap</small>
                  </div>
                </div>
              ))}
              <div className="sub" style={{ fontSize: 12 }}>
                <span className="dot" style={{ background: 'var(--brand)' }} /> Logged <span className="dot" style={{ background: '#F6C9D2', marginLeft: 10 }} /> Not logged
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="hd">
              <h3>Plan against what happened</h3>
            </div>
            <div className="bd" style={{ overflowX: 'auto' }}>
              {/* Five columns in a half-width card: tighter cell padding so "Not filed (i)" fits, as in the prototype. */}
              <table>
                <thead>
                  <tr>
                    <th style={TP}>Person</th>
                    <th className="tr" style={TP}>Hours vs plan</th>
                    <th className="tr" style={TP}>Tasks done</th>
                    <th className="tr" style={TP}>Filed late</th>
                    <th className="tr" style={TP}><Lbl>Not filed</Lbl></th>
                  </tr>
                </thead>
                <tbody>
                  {A.ppl.map((p) => (
                    <tr key={p.user.id}>
                      <td style={TP}>{firstName(p.user.name)}</td>
                      <td className="tr num" style={TP}>{p.planned ? `${Math.round((p.logged / p.planned) * 100)}%` : '-'}</td>
                      <td className="tr num" style={TP}>{p.tasksPlanned ? `${Math.round((p.tasksDone / p.tasksPlanned) * 100)}%` : '-'}</td>
                      <td className="tr num" style={TP}>{p.late}</td>
                      <td className="tr num" style={TP}>{p.missing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {pays.length ? (
        <div className="panel" style={{ marginTop: 22 }}>
          <div className="hd">
            <h3>Hours against what the client pays</h3>
            <div className="r">Most effort for the money, first</div>
          </div>
          <div className="bd">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th className="tr">Hours</th>
                  <th className="tr">Pays a month</th>
                  <th className="tr">Hours per ₹1 lakh</th>
                </tr>
              </thead>
              <tbody>
                {pays.slice(0, 8).map((x) => (
                  <tr key={x.r.client.id}>
                    <td>
                      <span className="split" style={{ gap: 8 }}>
                        <ClientLogo id={x.r.client.id} name={x.r.client.name} size={24} />
                        <span>{x.r.client.name}</span>
                      </span>
                    </td>
                    <td className="tr num">{fh(x.r.hours)}</td>
                    <td className="tr num">₹{r1(x.mrr!)} L</td>
                    <td className="tr num">
                      <b>{r1(x.hpl)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="sub" style={{ fontSize: 12, marginTop: 8 }}>
              A high number means the team spends a lot of time for little revenue. Worth a look at how that client is served.
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
